import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, Input } from '@angular/core';
import { MdIconComponent } from '@inditex/mduikit';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

import { ApplicationSections } from '@core/domain/models/application-section.model';
import { getPage } from '@core/infrastructure/state/core.selectors';
import { isSearchPage } from '@features/category/application/services/category.utils';
import { Resolutions } from '@layout/domain/models/layout.models';
import { OpenSearchFromHeader } from '@layout/infrastructure/state/layout.actions';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { CloseMenu, OpenMenu } from '@shared/menu-categories/store/menu-categories.actions';
import { GoToHome, GoToSection } from '@shared/navigation';
import {
    PublicImpl as ShopCartPublicImpl,
    PublicTokens as ShopCartPublicTokens,
    PublicPorts as ShopCartPublicPorts,
} from '@shared/shopcart/infrastructure/public/public-api';

@Component({
    selector: 'app-header-topbar-sm-2024',
    templateUrl: './header-topbar-sm-2024.component.html',
    styleUrls: ['./header-topbar-sm-2024.component.scss'],
    standalone: true,
    imports: [
        TranslateModule,
        AsyncPipe,
        MdIconComponent,
    ],
    providers: [
        {
            provide: ShopCartPublicTokens.SHOPPING_CART_SIDEBAR_FACADE,
            useFactory: (store) => new ShopCartPublicImpl.ShoppingCartSidebarFacade(store),
            deps: [Store],
        },
    ],
})
export class HeaderTopbarSm2024Component {
    private readonly store: Store = inject(Store);

    private readonly abTestingService: ABTestingService = inject(ABTestingService);

    // eslint-disable-next-line max-len
    private readonly shoppingCartSidebarFacade: ShopCartPublicPorts.IShoppingCartSidebarCommandFacade = inject(ShopCartPublicTokens.SHOPPING_CART_SIDEBAR_FACADE);

    private readonly getResolution$$ = this.store.selectSignal(getResolution);

    private readonly isMobile$$ = computed(() => [Resolutions.MOBILE, Resolutions.TABLET].includes(this.getResolution$$()));

    @Input()
    public icon: 'bars' | 'close' = 'bars';

    @Input()
    public hiddenLogo = false;

    @Input()
    public cartItems = 0;

    @Input()
    public isOpenForSale = false;

    @Input()
    public isLogged = false;

    @Input()
    public lastSection: any = ApplicationSections.WOMEN;

    @Input()
    public latestHomeSeoUrl = '';

    public openMenu() {
        if (typeof hj !== 'undefined') {
            hj('tagRecording', ['open_menu_sidebar']);
        }
        this.store.dispatch(OpenMenu());
    }

    public closeMenu() {
        this.store.dispatch(CloseMenu());
    }

    public goToHome($event: MouseEvent) {
        $event.preventDefault();
        this.store.dispatch(GoToHome({}));
        this.closeMenu();
    }

    public openSearch() {
        this.store.dispatch(OpenSearchFromHeader({ from: 'header' }));
        this.closeMenu();
    }

    public login() {
        if (!this.isLogged) {
            this.store.dispatch(HeaderEventAnalytics({ section: 'login' }));
            this.store.dispatch(GoToSection({ url: 'userLoginApolo' }));
        } else {
            this.store.dispatch(HeaderEventAnalytics({ section: 'account' }));
            this.store.dispatch(GoToSection({ url: 'myAccount' }));
        }
        this.closeMenu();
    }

    public openShopCart(): void {
        this.closeMenu();
        this.store.dispatch(HeaderEventAnalytics({ section: 'cart' }));

        const goToShopCartConditions = this.isMobile$$()
            // Test AB ECOMDUTI-16388 Reducir pasos minicesta
            || this.abTestingService.getShopCartStepVariant('goToShopCartFromHeaderButton');
        if (goToShopCartConditions) {
            this.store.dispatch(GoToSection({ url: 'shopCart', reload: true }));
            return;
        }

        this.shoppingCartSidebarFacade.openMiniCartInSidebar();
    }

    public isSearchPage$: Observable<boolean> = this.store.select(getPage).pipe(
        map((page) => isSearchPage(page)),
    );
}
